import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { Company, User } from 'lib-shared-types-api';

import { userModalActions } from './userModal.actions';

export type UserModalState = {
  meta: {
    asyncStatus: AsyncStatus;
  };
  ui: {
    isUserModalShown: boolean;
  };
  companies: Company[];
  /** The new company created using select component */
  newCompany: Company | null;
  user: User | null;
  userId: string | null;
};

export const initialState: UserModalState = {
  meta: {
    asyncStatus: 'succeeded',
  },
  ui: {
    isUserModalShown: false,
  },
  user: null,
  companies: [],
  newCompany: null,
  userId: null,
};

export const userModalReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(userModalActions.show, (state, action) => {
      const { user } = action.payload;
      state.ui.isUserModalShown = true;
      state.userId = user && user.id ? user.id : null;
      state.user = user ? (user as User) : null;
    })
    .addCase(userModalActions.hide, (state) => {
      state.ui.isUserModalShown = false;
      state.user = null;
      state.userId = null;
      state.newCompany = null;
      state.companies = [];
    })
    .addCase(userModalActions.user.get.fulfilled, (state, action) => {
      const user = action.payload.data;
      if (user) {
        state.userId = user && user.id ? user.id : null;
        state.user = user ? user : null;
      }
    })
    .addCase(userModalActions.user.update.fulfilled, (state, action) => {
      const user = action.payload.data;
      if (user && state.userId === user.id) {
        state.userId = user.id;
        state.user = user;
      }
    })
    // Get all companies result (support recusive fetch).
    .addCase(userModalActions.companies.fulfilled, (state, action) => {
      for (const company of action.payload.data.companies) {
        const foundCompanyIndex = state.companies.findIndex(
          (item) => item.id === company.id,
        );
        if (foundCompanyIndex > -1) {
          state.companies[foundCompanyIndex] = company;
        } else {
          state.companies.push(company);
        }
      }
    })
    .addCase(userModalActions.companies.rejected, (state) => {
      state.companies = [];
    })
    .addCase(userModalActions.companies.add.fulfilled, (state, action) => {
      state.newCompany = action.payload.company;
      state.companies.push(action.payload.company);
    })
    .addMatcher(
      isAnyOf(
        userModalActions.user.get.pending,
        userModalActions.user.add.pending,
        userModalActions.user.update.pending,
        userModalActions.companies.get,
        userModalActions.companies.add.pending,
      ),
      (state) => {
        state.meta.asyncStatus = 'loading';
      },
    )
    .addMatcher(
      isAnyOf(
        userModalActions.user.get.fulfilled,
        userModalActions.user.add.fulfilled,
        userModalActions.user.update.fulfilled,
        userModalActions.companies.fulfilled,
        userModalActions.companies.add.fulfilled,
      ),
      (state) => {
        state.meta.asyncStatus = 'succeeded';
      },
    )
    .addMatcher(
      isAnyOf(
        userModalActions.user.get.rejected,
        userModalActions.user.add.rejected,
        userModalActions.user.update.rejected,
        userModalActions.companies.rejected,
        userModalActions.companies.add.rejected,
      ),
      (state) => {
        state.meta.asyncStatus = 'failed';
      },
    ),
);
